import { KatalogRah, OpsiRah, RekamAsuhanHemodialisis } from "@/types/rekam_asuhan_hemodialisis";

export interface SoapAsuhanHd {
  s: string;
  o: string;
  a: string;
  p: string;
}

function ada(v: unknown): boolean {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

function gabung(parts: unknown[], pemisah: string): string {
  return parts
    .filter(ada)
    .map(p => String(p).trim())
    .join(pemisah);
}

function label(opsi: OpsiRah[], nilai: string | null): string {
  if (!nilai) return "";

  return opsi.find(it => it.value === nilai)?.text || "";
}

function labelBanyak(opsi: OpsiRah[], nilai: string[]): string {
  return gabung(
    nilai.map(v => label(opsi, v)),
    ", "
  );
}

function baris(judul: string, isi: string): string {
  return ada(isi) ? `${judul}: ${isi}` : "";
}

function dengan(nilai: unknown, satuan: string): string {
  return ada(nilai) ? `${nilai} ${satuan}` : "";
}

function dalamKurung(isi: string): string {
  return ada(isi) ? `(${isi})` : "";
}

function kategoriNyeri(skala: number): string {
  if (skala === 0) return "Tidak nyeri";
  if (skala <= 3) return "Ringan";
  if (skala <= 6) return "Sedang";

  return "Berat";
}

function subjective(doc: RekamAsuhanHemodialisis, katalog: KatalogRah): string {
  const { keluhan, nyeri } = doc.asesmen;
  const daftarKeluhan = gabung(
    [labelBanyak(katalog.keluhan, keluhan.items.filter(it => it !== "lain")), keluhan.lain],
    ", "
  );

  let teksNyeri = "";
  if (nyeri.ada === "tidak") {
    teksNyeri = "Tidak ada";
  } else if (nyeri.ada === "ya") {
    teksNyeri = gabung(
      [
        "Ada",
        nyeri.skala !== null ? `skala ${nyeri.skala} (${kategoriNyeri(nyeri.skala)})` : "",
        label(katalog.nyeri_jenis, nyeri.jenis),
        nyeri.lokasi ? `lokasi ${nyeri.lokasi}` : "",
        nyeri.durasi ? `durasi ${nyeri.durasi}` : ""
      ],
      ", "
    );
  }

  return gabung([baris("Keluhan utama", daftarKeluhan), baris("Nyeri", teksNyeri)], "\n");
}

function skorRisikoJatuh(doc: RekamAsuhanHemodialisis, katalog: KatalogRah): string {
  let total = 0;
  let terisi = false;
  katalog.risiko_jatuh.forEach(item => {
    const pilihan = item.opsi.find(it => it.value === doc.asesmen.risiko_jatuh[item.key]);
    if (pilihan) {
      total += pilihan.skor;
      terisi = true;
    }
  });
  if (!terisi) return "";

  const kategori = katalog.risiko_jatuh_kategori.find(it => total >= it.min && (it.max === null || total <= it.max));

  return kategori ? `skor ${total} (${kategori.label})` : `skor ${total}`;
}

function cairan(doc: RekamAsuhanHemodialisis): string {
  let intake: number | null = null;
  doc.monitoring.forEach(row => {
    [row.intake_nacl, row.intake_dextrose, row.intake_makan_minum, row.intake_lain].forEach(v => {
      if (v !== null) intake = (intake || 0) + v;
    });
  });
  const balance = intake !== null && doc.total_uf !== null ? intake - doc.total_uf : null;

  return gabung(
    [
      intake !== null ? `intake ${intake} ml` : "",
      doc.total_uf !== null ? `total UF ${doc.total_uf} ml` : "",
      balance !== null ? `balance ${balance} ml` : ""
    ],
    ", "
  );
}

function objective(doc: RekamAsuhanHemodialisis, katalog: KatalogRah): string {
  const f = doc.asesmen.fisik;
  const gizi = doc.asesmen.gizi;
  const penyulit = gabung(
    [labelBanyak(katalog.penyulit, doc.penyulit.items.filter(it => it !== "lain")), doc.penyulit.lain],
    ", "
  );
  const misAmbang = katalog.mis_ambang_malnutrisi;

  return gabung(
    [
      gabung(
        [
          baris("KU", f.keadaan_umum === "lain" ? f.keadaan_umum_lain || "" : label(katalog.keadaan_umum, f.keadaan_umum)),
          baris("Kesadaran", label(katalog.kesadaran, f.kesadaran))
        ],
        ", "
      ),
      gabung(
        [
          baris("TD", dengan(f.tekanan_darah, "mmHg")),
          baris(
            "Nadi",
            gabung([dengan(f.nadi_frek, "x/menit"), dalamKurung(label(katalog.nadi_irama, f.nadi_irama))], " ")
          ),
          baris("RR", gabung([dengan(f.respirasi_frek, "x/menit"), dalamKurung(labelBanyak(katalog.respirasi, f.respirasi))], " ")),
          baris("Suhu", dengan(f.suhu, "°C"))
        ],
        ", "
      ),
      gabung(
        [
          baris(
            "Konjungtiva",
            f.konjungtiva === "lain" ? f.konjungtiva_lain || "" : label(katalog.konjungtiva, f.konjungtiva)
          ),
          baris("Ekstremitas", labelBanyak(katalog.ekstremitas, f.ekstremitas))
        ],
        ", "
      ),
      gabung(
        [
          baris("BB pre HD", dengan(f.bb_pre, "kg")),
          baris("BB HD yang lalu", dengan(f.bb_hd_lalu, "kg")),
          baris("BB kering", dengan(f.bb_kering, "kg")),
          baris("BB post HD", dengan(f.bb_post, "kg"))
        ],
        ", "
      ),
      baris(
        "Akses vaskular",
        f.akses_vaskuler === "lainnya"
          ? f.akses_vaskuler_lain || ""
          : gabung([label(katalog.akses_vaskuler, f.akses_vaskuler), label(katalog.kateter_lokasi, f.kateter_lokasi)], " ")
      ),
      baris("Risiko jatuh", skorRisikoJatuh(doc, katalog)),
      baris(
        "Gizi",
        gabung(
          [
            gizi.mis_skor !== null
              ? `MIS ${gizi.mis_skor} (${gizi.mis_skor >= misAmbang ? "malnutrisi" : "tanpa malnutrisi"})`
              : "",
            gizi.sga_skor !== null ? `SGA ${gizi.sga_skor}` : ""
          ],
          ", "
        )
      ),
      baris("Penunjang", doc.asesmen.penunjang || ""),
      baris("Cairan", cairan(doc)),
      baris("Penyulit selama HD", penyulit)
    ],
    "\n"
  );
}

function plan(doc: RekamAsuhanHemodialisis, katalog: KatalogRah): string {
  const i = doc.instruksi;
  const jenisHd = i.jenis_hd === "lain" ? i.jenis_hd_lain || "" : label(katalog.jenis_hd, i.jenis_hd);
  const resep = gabung(
    [
      jenisHd ? `HD ${jenisHd}` : "",
      dengan(i.durasi_jam, "jam"),
      baris("QB", dengan(i.qb, "ml/mnt")),
      baris("QD", dengan(i.qd, "ml/mnt")),
      baris("UF goal", dengan(i.uf_goal, "ml")),
      baris("Profiling Na", i.profiling_na || ""),
      baris("Profiling UF", i.profiling_uf || ""),
      baris("Profiling bicarbonat", i.profiling_bicarbonat || ""),
      baris("Dialisat", label(katalog.dialisat, i.dialisat)),
      baris("Conductivity", i.conductivity || ""),
      baris("Temperatur", i.temperatur || "")
    ],
    ", "
  );
  const heparin = i.tanpa_heparin
    ? gabung(["Tanpa heparin", i.tanpa_heparin_penyebab ? `(${i.tanpa_heparin_penyebab})` : ""], " ")
    : gabung(
        [
          baris("sirkulasi", dengan(i.heparin_sirkulasi, "iu")),
          baris("awal", dengan(i.heparin_awal, "iu")),
          i.heparin_maintenance
            ? baris(
                "maintenance",
                gabung(
                  [label(katalog.heparin_maintenance, i.heparin_maintenance), dengan(i.heparin_maintenance_dosis, "iu/jam")],
                  " "
                )
              )
            : "",
          baris("LMWH", i.lmwh || "")
        ],
        ", "
      );
  const obat = gabung(
    doc.catatan_medis.map(it => gabung([it.obat, it.catatan ? `(${it.catatan})` : ""], " ")),
    "; "
  );

  return gabung(
    [
      baris("Resep HD", resep),
      baris("Heparinisasi", heparin),
      i.program_bilas ? "Program bilas NaCl 0,9% 100 cc/jam/½ jam" : "",
      baris("Catatan", i.catatan_lain || ""),
      baris("Obat & catatan medis", obat)
    ],
    "\n"
  );
}

export function soapDariAsuhanHd(doc: RekamAsuhanHemodialisis, katalog: KatalogRah): SoapAsuhanHd {
  return {
    s: subjective(doc, katalog),
    o: objective(doc, katalog),
    a: baris("Dx. Medis", doc.diagnosa_medis || ""),
    p: plan(doc, katalog)
  };
}
